import React, { type AllHTMLAttributes, type PropsWithChildren } from 'react';
import cx from 'classnames';
import stylesheet from './SkeletonLoader.css';

type SkeletonLoaderBaseProps = {
  /** Additional class names to apply to the component */
  className?: string;
  /** Background color variant */
  variant?: 'primary' | 'secondary';
  /** Whether to show the pulse animation */
  animate?: boolean;
} & Omit<AllHTMLAttributes<HTMLDivElement>, 'size'>;

export type SkeletonLoaderContainerProps =
  PropsWithChildren<SkeletonLoaderBaseProps>;

export type SkeletonLoaderControlProps = SkeletonLoaderBaseProps & {
  /** Size of the control */
  size?: 'small' | 'medium' | 'large';
};

export type SkeletonLoaderTextProps = SkeletonLoaderBaseProps & {
  /** Size of the text */
  size?: 'small' | 'medium' | 'large';
};

export type SkeletonLoaderIconProps = SkeletonLoaderBaseProps & {
  /** Size of the icon */
  size?: 'small' | 'medium' | 'large';
};

const SkeletonLoaderContainer = React.forwardRef<
  HTMLDivElement,
  SkeletonLoaderContainerProps
>(function SkeletonLoaderContainer(
  { className, variant = 'primary', animate = true, ...rest },
  forwardedRef,
) {
  return (
    <div
      className={cx(
        stylesheet.container,
        stylesheet[variant],
        { [stylesheet.animate]: animate },
        className,
      )}
      ref={forwardedRef}
      role="img"
      {...rest}
    />
  );
});

const SkeletonLoaderControl = React.forwardRef<
  HTMLDivElement,
  SkeletonLoaderControlProps
>(function SkeletonLoaderControl(
  { className, size = 'medium', variant = 'primary', animate = true, ...rest },
  forwardedRef,
) {
  return (
    <div
      className={cx(
        stylesheet.control,
        stylesheet[`control-${size}`],
        stylesheet[variant],
        { [stylesheet.animate]: animate },
        className,
      )}
      ref={forwardedRef}
      role="img"
      {...rest}
    />
  );
});

const SkeletonLoaderText = React.forwardRef<
  HTMLDivElement,
  SkeletonLoaderTextProps
>(function SkeletonLoaderText(
  { className, size = 'medium', variant = 'primary', animate = true, ...rest },
  forwardedRef,
) {
  return (
    <div
      className={cx(
        stylesheet.text,
        stylesheet[`text-${size}`],
        stylesheet[variant],
        { [stylesheet.animate]: animate },
        className,
      )}
      ref={forwardedRef}
      role="img"
      {...rest}
    />
  );
});

const SkeletonLoaderIcon = React.forwardRef<
  HTMLDivElement,
  SkeletonLoaderIconProps
>(function SkeletonLoaderIcon(
  { className, size = 'medium', variant = 'primary', animate = true, ...rest },
  forwardedRef,
) {
  return (
    <div
      className={cx(
        stylesheet.icon,
        stylesheet[`icon-${size}`],
        stylesheet[variant],
        { [stylesheet.animate]: animate },
        className,
      )}
      ref={forwardedRef}
      role="img"
      {...rest}
    />
  );
});

export {
  SkeletonLoaderContainer,
  SkeletonLoaderControl,
  SkeletonLoaderText,
  SkeletonLoaderIcon,
};
